import { useQuery } from "@apollo/client";
import { useCanViewPersonCareerBool, useCanViewPersonMembershipBool } from "@/hooks/useCapabilities";
import { Person } from "@/types";
import { usePersonContactAccess } from "./_usePersonContactAccess";
import { PERSON_CONTACT_MEMBERSHIP_QUERY, PERSON_CONTACT_CAREER_QUERY, PERSON_DETAILS_QUERY } from "./queries";

type PersonData = { person: Person };

export function usePersonContactDetailsQuery(pgaId?: string) {
  const { loaded, canViewPersonContact } = usePersonContactAccess();
  const canViewPersonMembership = useCanViewPersonMembershipBool();
  const canViewPersonCareer = useCanViewPersonCareerBool();

  const skip = !loaded || !canViewPersonContact || !pgaId;
  const { data, error } = useQuery<PersonData>(PERSON_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip
  });
  const { data: membershipData, loading: membershipLoading } = useQuery(PERSON_CONTACT_MEMBERSHIP_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    skip: skip || !canViewPersonMembership,
    fetchPolicy: "network-only"
  });

  const { data: careerData, loading: careerLoading } = useQuery(PERSON_CONTACT_CAREER_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    skip: skip || !canViewPersonCareer,
    fetchPolicy: "network-only"
  });

  const membershipReady = !canViewPersonMembership || !membershipLoading;
  const careerReady = !canViewPersonCareer || !careerLoading;
  const person =
    data?.person && membershipReady && careerReady
      ? {
          ...data.person,
          ...(membershipData?.person ? { latestProgram: membershipData.person.latestProgram } : {}),
          ...(careerData?.person?.employments !== undefined ? { employments: careerData.person.employments } : {})
        }
      : undefined;

  return { loaded, canViewPersonContact, person, error };
}
